/**
 * Train service for interacting with the train worker API
 */

import { train } from '@/config/api';

/**
 * Search for trains between two stations
 * @param {string} from - Departure station
 * @param {string} to - Arrival station
 * @returns {Promise<Object>} - Train search results
 * @throws {Error} - If the request fails or stations are invalid
 */
export async function searchTrains(from, to) {
  if (!from || !to) {
    throw new Error('Both from and to stations are required');
  }

  // Clean up station names (remove extra spaces, convert to lowercase)
  const fromStation = from.trim();
  const toStation = to.trim();

  try {
    // Call the train worker API
    const response = await fetch(`${train}/trains?from=${encodeURIComponent(fromStation)}&to=${encodeURIComponent(toStation)}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to fetch train data: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while searching for trains');
  }
}

/**
 * Search for trains using direct path format
 * @param {string} from - Departure station
 * @param {string} to - Arrival station
 * @returns {Promise<Object>} - Train search results
 * @throws {Error} - If the request fails or stations are invalid
 */
export async function searchTrainsByDirectPath(from, to) {
  if (!from || !to) {
    throw new Error('Both from and to stations are required');
  }

  // Clean up station names (remove extra spaces, convert to lowercase)
  const fromStation = from.trim().toLowerCase();
  const toStation = to.trim().toLowerCase();

  try {
    // Call the train worker API using direct path format
    const response = await fetch(`${train}/${fromStation}-${toStation}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to fetch train data: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while searching for trains');
  }
}
